import { onNuxtReady, useAsyncData, useState } from '#imports'
import { type Reactive, type Ref, computed } from 'vue'

import type { AlgoliaProduct } from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia'
import type { SearchConfiguration } from '@backmarket/http-api/src/api-specs-search-reco/search/searchConfiguration'
import { useAlgoliaSearchOptions } from '@backmarket/nuxt-layer-algolia/useAlgoliaSearchOptions'
import { useTestChallenge } from '@backmarket/nuxt-layer-algolia/useRedirectToTestChallenge'
import { useBuybackEnabled } from '@backmarket/nuxt-layer-buyback/app/composables/useBuybackEnabled/useBuybackEnabled'
import { buildUiFilters } from '@backmarket/nuxt-layer-recommendation/filtering/buildUiFilters'
import { filtersToWhereClause } from '@backmarket/nuxt-layer-recommendation/filtering/filtersToWhereClause'
import { getGraphFacet } from '@backmarket/nuxt-layer-recommendation/filtering/getGraphFacet'
import {
  algoliaHitToProduct,
  algoliaHitToVariant,
} from '@backmarket/nuxt-layer-recommendation/helpers.ts'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import algoliasearch from 'algoliasearch'

import type {
  ProductsSearchData,
  SearchTrackingData,
} from './useProductsSearch.types'

const DEFAULT_DATA: ProductsSearchData = {
  products: [],
  total: 0,
  pageCount: 0,
  facets: [],
  priceFacet: undefined,
}

export function useProductsSearch(
  identifier: string,
  configuration: SearchConfiguration,
  searchParams: {
    query?: Ref<string>
    filtersInput: Reactive<Record<string, string[]>>
    sortInput: Ref<string>
    pageInput: Ref<number>
    priceInput: Ref<[number, number] | undefined>
  },
  options?: { paused?: Ref<boolean> },
) {
  const searchTrackingData = useState<SearchTrackingData>(
    'search-query',
    () => ({
      id: '',
      index: '',
    }),
  )
  const algoliaSearchOption = useAlgoliaSearchOptions('search')
  const algoliaClient = algoliasearch('dummy_app_id', 'dummy_key', {
    ...algoliaSearchOption,
  })
  const logger = useLogger()
  const { redirectToTestChallengePage, isTestChallengeError } =
    useTestChallenge()
  const { isBuybackEnabled } = useBuybackEnabled()

  // Search Configuration
  const index = computed(() => {
    return (
      [
        configuration.indexes.active,
        ...(configuration.indexes?.other ?? []),
      ].find(({ name }) => name === searchParams.sortInput.value) ??
      configuration.indexes.active
    )
  })

  const requestOptions = computed(() => {
    const defaultFacets = ['price', 'page', 'q', 'sort']
    const sidebarFacets = configuration.facets.map(({ name }) => name)
    const hitsPerPage = isBuybackEnabled ? 31 : 32
    const priceFacets =
      configuration.priceFacet?.scales.map((facet) => facet.facetName) ?? []

    return {
      ...index.value.queryParams,
      filters: filtersToWhereClause(
        searchParams.filtersInput,
        configuration.complexFilter,
      ),
      facets: [...defaultFacets, ...sidebarFacets, ...priceFacets],
      numericFilters: searchParams.priceInput.value
        ? [
            `price>=${searchParams.priceInput.value[0]}`,
            `price<=${searchParams.priceInput.value[1]}`,
          ]
        : undefined,
      page: searchParams.pageInput.value - 1,
      hitsPerPage,
    }
  })

  const experiments = useExperiments()
  const withFreshDataRefresh =
    experiments['experiment.plpDuplicatedAlgoliaCalls'] ===
    'withDuplicatedCalls'

  const asyncData = useAsyncData(
    getAsyncDataKey('products-search', {
      identifier,
      index: index.value.name,
    }),
    async () => {
      if (options?.paused?.value) return null

      try {
        const algoliaIndex = algoliaClient.initIndex(index.value.name)
        const result = await algoliaIndex.search<AlgoliaProduct>(
          searchParams.query?.value ?? '',
          requestOptions.value,
        )

        logger.info('[S&R] PLP search query success', {
          endpointName: 'algoliaSearch',
          type: 'API_REQUEST_SUCCESS',
          searchProcessingTime: result.processingTimeMS,
          owners: ['bot-squad-search-and-recommendation-front'],
        })

        if (result.nbHits === 0) {
          logger.info('[S&R] PLP returned empty results', {
            type: 'EMPTY_SEARCH_RESULTS',
            owners: ['bot-squad-search-and-recommendation-front'],
          })
        }

        searchTrackingData.value = {
          id: result.queryID,
          index: index.value.name,
        }

        return result
      } catch (e) {
        logger.error('[S&R] PLP search query failed', {
          endpointName: 'algoliaSearch',
          type: 'API_REQUEST_FAIL',
          e,
          owners: ['bot-squad-search-and-recommendation-front'],
        })

        if (isTestChallengeError(e)) {
          redirectToTestChallengePage()
        }

        throw e
      }
    },
    {
      default: () => DEFAULT_DATA,
      transform: (result) => {
        if (!result) return DEFAULT_DATA

        return {
          total: result.nbHits,
          pageCount: result.nbPages,
          products:
            configuration.productGroupingType === 'parent'
              ? result.hits.map(algoliaHitToProduct)
              : result.hits.map(algoliaHitToVariant),
          facets: buildUiFilters([], configuration.facets, result.facets),
          priceFacet:
            configuration.priceFacet &&
            result.facets &&
            getGraphFacet(configuration.priceFacet, result.facets, {
              avg: result.facets_stats?.price.avg ?? 0,
              max: result.facets_stats?.price.max ?? 0,
            }),
        }
      },
      lazy: false,
      watch: [() => index.value.name, requestOptions],
    },
  )

  if (import.meta.client && withFreshDataRefresh) {
    const { refresh } = asyncData
    onNuxtReady(() => {
      refresh()
    })
  }

  return asyncData
}
